import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, TableSkeleton } from "@/components/shared";
import CandidateForm from "@/components/candidates/CandidateForm";
import BulkResumeImport from "@/components/candidates/BulkResumeImport";
import { initials, fmtDate, label } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Download, Users, ChevronLeft, ChevronRight, Upload } from "lucide-react";

export default function Candidates() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [region, setRegion] = useState("all");
  const [page, setPage] = useState(1);
  const [facets, setFacets] = useState({ statuses: [], regions: [] });
  const [formOpen, setFormOpen] = useState(params.get("new") === "1");
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetch = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/candidates", {
        params: {
          q: q || undefined,
          status: status !== "all" ? status : undefined,
          region: region !== "all" ? region : undefined,
          page, page_size: 15,
        },
      });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); setData({ items: [], total: 0, pages: 0 }); }
  }, [q, status, region, page]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { api.get("/candidates/facets").then(({ data }) => setFacets(data)).catch(() => {}); }, []);

  const onSearch = (v) => { setQ(v); setPage(1); };

  const exportCsv = async () => {
    try {
      const res = await api.get("/candidates/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "candidates.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(apiErr(e, "Export not permitted")); }
  };

  return (
    <div>
      <PageHeader
        title="Candidates"
        subtitle={data ? `${data.total} candidate${data.total === 1 ? "" : "s"} in your database` : "Candidate 360 database"}
        actions={
          <>
            {can("candidate:export") && (
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="candidates-export-button" className="gap-1.5">
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {can("candidate:create") && (
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="candidates-bulk-import-button" className="gap-1.5">
                <Upload className="h-4 w-4" /> Bulk Resumes
              </Button>
            )}
            {can("candidate:create") && (
              <Button size="sm" onClick={() => setFormOpen(true)} data-testid="candidates-create-button"
                className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white">
                <Plus className="h-4 w-4" /> New Candidate
              </Button>
            )}
          </>
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="candidate-search-input" placeholder="Search name, title, skills, email…"
              value={q} onChange={(e) => onSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="candidate-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {facets.statuses.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={region} onValueChange={(v) => { setRegion(v); setPage(1); }}>
            <SelectTrigger className="w-36" data-testid="candidate-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {facets.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {!data ? (
          <div className="p-4"><TableSkeleton /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={Users} title="No candidates found"
            description="Adjust your filters or add a new candidate to get started."
            action={can("candidate:create") && <Button onClick={() => setFormOpen(true)} className="bg-[#255058] text-white">Add candidate</Button>} />
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead className="hidden md:table-cell">Location</TableHead>
                  <TableHead className="hidden lg:table-cell">Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Owner</TableHead>
                  <TableHead className="hidden xl:table-cell">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((c) => (
                  <TableRow key={c.id} data-testid={`candidate-row-${c.id}`}
                    className="cursor-pointer hover:bg-accent/40" onClick={() => navigate(`/candidates/${c.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9"><AvatarFallback className="bg-[#255058] text-white text-xs">{initials(c.full_name)}</AvatarFallback></Avatar>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.full_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{c.current_title || "—"}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{c.location || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{c.region}</TableCell>
                    <TableCell><StatusBadge value={c.status} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{c.owner_name || "—"}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">{fmtDate(c.last_activity_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="candidates-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="candidates-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <BulkResumeImport open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={fetch} />

      <CandidateForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setParams({}); }}
        onSaved={(c) => { setFormOpen(false); setParams({}); fetch(); if (c?.id) navigate(`/candidates/${c.id}`); }}
      />
    </div>
  );
}
